/**
 * EPI Edit Page
 * Page for editing existing EPI
 *
 * @version 2025.2.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { epiService } from '../../services/epiService';
import { Epi, EpiFormData } from '../../types/epi';
import { EPIFormWizard } from './components';

export const EPIEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [epi, setEpi] = useState<Epi | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEpi = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await epiService.getEpiById(id);
        setEpi(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar EPI');
      } finally {
        setLoading(false);
      }
    };

    loadEpi();
  }, [id]);

  const handleSubmit = async (data: EpiFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await epiService.updateEpi(id, data);
      navigate('/epis');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar EPI');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/epis');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'EPIs', path: '/epis' },
    { label: 'Editar EPI' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar EPI">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!epi) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar EPI">
        <Alert severity="error">EPI não encontrado</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={breadcrumbs} title="Editar EPI" subtitle={epi.nome}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader title="Edição de EPI" />
        <CardContent>
          <EPIFormWizard
            initialData={epi}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default EPIEditPage;
